import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';

import { createRockDependency, deleteRockDependency, type Rock } from '@/api/eos/rocks.api';
import { RockStatusBadge } from '@/components/eos/RockStatusBadge';
import { describeEosError, eosErrorCode } from '@/lib/eos/errors';
import { useToast } from '@/hooks/useToast';

/**
 * Dependencies section of the rock detail drawer (Phase H — Rocks depth).
 *
 * Lists prerequisite rocks with their status, shows a "Blocked" banner when
 * any prerequisite isn't done, and offers a same-quarter picker to add one.
 * Mutations invalidate `['eos','rocks']`. The dependency-specific server
 * codes are mapped to friendly copy here (the shared describer doesn't own
 * them).
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

function describeDependencyError(err: unknown, fallback: string): string {
  switch (eosErrorCode(err)) {
    case 'self_dependency':
      return 'A rock cannot depend on itself.';
    case 'dependency_cycle':
      return 'That would create a dependency loop — pick a different prerequisite.';
    case 'dependency_rock_not_found':
      return 'That rock no longer exists — refresh the page.';
    case 'dependency_exists':
      return 'That prerequisite is already on the list.';
    default:
      return describeEosError(err, fallback);
  }
}

export function RockDependencies({
  rock,
  allRocks,
  canEdit,
}: {
  rock: Rock;
  allRocks: Rock[];
  canEdit: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [picker, setPicker] = useState('');

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'rocks'] });
  }

  const addMutation = useMutation({
    mutationFn: (dependsOnRockId: string) => createRockDependency(rock.id, { dependsOnRockId }),
    onSuccess: () => {
      setPicker('');
      invalidate();
    },
    onError: (err) => {
      setPicker('');
      toast.show(describeDependencyError(err, 'Could not add the dependency.'), { variant: 'danger' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (dependencyId: string) => deleteRockDependency(rock.id, dependencyId),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeDependencyError(err, 'Could not remove the dependency.'), { variant: 'danger' });
    },
  });

  const dependsOnIds = new Set(rock.dependencies.map((d) => d.dependsOnRockId));
  // Same-quarter rocks only (the page already lists one quarter), minus self
  // and anything already a prerequisite.
  const options = allRocks.filter((r) => r.id !== rock.id && !dependsOnIds.has(r.id));
  const notDone = rock.dependencies.filter((d) => d.dependsOnRock.status !== 'done');

  return (
    <div className="space-y-3">
      <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Dependencies</h3>

      {rock.blocked ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-warning-text" />
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-kw-warning-text">Blocked</p>
            <p className="mt-0.5 text-[12px] text-kw-warning-text/90">
              Waiting on {notDone.map((d) => d.dependsOnRock.title).join(', ')}.
            </p>
          </div>
        </div>
      ) : null}

      {rock.dependencies.length === 0 ? (
        <p className="text-[12px] text-kw-muted">No prerequisites — this rock isn’t waiting on anything.</p>
      ) : (
        <ul className="space-y-1.5">
          {rock.dependencies.map((d) => (
            <li key={d.id} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-[13px] text-kw-shark">
                {d.dependsOnRock.title}
              </span>
              <RockStatusBadge status={d.dependsOnRock.status} />
              {canEdit ? (
                <button
                  type="button"
                  aria-label={`Remove dependency ${d.dependsOnRock.title}`}
                  onClick={() => {
                    removeMutation.mutate(d.id);
                  }}
                  className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                >
                  <X className="size-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        options.length > 0 ? (
          <select
            aria-label="Add a prerequisite"
            value={picker}
            onChange={(e) => {
              const value = e.target.value;
              setPicker(value);
              if (value) addMutation.mutate(value);
            }}
            className={`${SELECT_CLS} w-full`}
          >
            <option value="">Add a prerequisite…</option>
            {options.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-[12px] text-kw-muted">No other rocks this quarter to depend on.</p>
        )
      ) : null}
    </div>
  );
}
